import * as React from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';

import { DEFAULT_TRANSLATABLE_NEWSLETTER_CONTENT, TranslatableNewsletterContent, TranslatedNewsletterContent } from 'modules/common/components/authoring/models';
import { ILanguage, ILcidTranslateDetails } from 'modules/localization/models';
import LanguageBarWithTranslateDialog from 'modules/common/components/languageBarWithTranslateDialog';
import { useLanguagesData } from 'modules/common/hooks/data/useLanguages';
import { TranslationContentType } from 'modules/common/models';
import TranslateAllIssueDialog from 'modules/settings/components/translateAllIssueDialog';
import useTranslateAll from 'modules/common/hooks/data/useTranslateAll';
import LoadingOverlay from 'modules/common/components/loadingOverlay';
import { cognitiveServicesApi } from 'api/instances';
import ErrorSnackbar from 'modules/common/components/snackbars/errorSnackbar';


const NewsletterLanguageBar: React.FunctionComponent<INewsletterLanguageBarProps> = props => {
    const translations = props.newsletterContent;
    const { availableLanguages, activeLcid, lcidMappings, defaultLcid } = props.useLanguagesData;
    const [errorMessage, setErrorMessage] = React.useState<string>("");

    const translateAllApiWrapper = async (toTranslate: TranslatableNewsletterContent, outputLanguages: ILcidTranslateDetails[]) => {
        return await cognitiveServicesApi.translateAllNewsletterLanguages(
            toTranslate, outputLanguages
        );
    }

    const { isFetchingTranslations, errorModalStatus, translateAll } = useTranslateAll({
        translatedContent: translations[defaultLcid],
        defaultLcid,
        availableLanguages,
        getAllTranslations: translateAllApiWrapper
    });

    const translationHasContent = (lcid: string) =>
        !!translations &&
        !!translations[lcid] &&
        (
            Boolean(translations[lcid].subject) || 
            Boolean(translations[lcid].body) || 
            Boolean(translations[lcid].footer) || 
            Boolean(translations[lcid].header) || 
            Boolean(translations[lcid].preheader)
        );

    /**
     * Handle translate click
     * - show dialog if translate will replace content
     */
    const onTranslate = async (inputLanguage: string, outputLcid?: string) => {
        if (!translations || !inputLanguage) return;

        const languageToTranslateTo = outputLcid ?? activeLcid;
        const outputLanguage = availableLanguages?.find((language: ILanguage) => language.lcid === languageToTranslateTo);

        // do the translation and update state
        if (outputLanguage) {
            props.setIsFetchingTranslation(true);

            try {
                const translation = await props.getNewsletterTranslation( 
                    translations[inputLanguage],
                    outputLanguage.translateAs,
                    inputLanguage,
                    props.newsletterId ?? "00000000-0000-0000-0000-000000000000"
                );
    
                let newTranslations = { ...translations };
                newTranslations[outputLanguage.lcid] = {
                    ...translation,
    
                    header: translation.header.length > 0 ? `<p><em>[${lcidMappings[outputLanguage.lcid].autoTranslateText}]</em></p>${translation.header}` : "",
                    body: translation.body.length > 0 ? `<p><em>[${lcidMappings[outputLanguage.lcid].autoTranslateText}]</em></p>${translation.body}` : "",
                    footer: translation.footer.length > 0 ? `<p><em>[${lcidMappings[outputLanguage.lcid].autoTranslateText}]</em></p>${translation.footer}` : "",
                };
    
                props.onChangeTranslatedContent(newTranslations);
            }
            catch (err) {
                setErrorMessage("Something went wrong, please try again.");
                throw err;
            }
            finally {
                props.setIsFetchingTranslation(false);
            }
        }
    };

    const requiredTranslationFieldsHasContent = (lcid: string) => 
        !!translations && 
        !!translations[lcid] && 
        !!translations[lcid].subject;

    const onTranslateAll = async (outputLanguages: ILcidTranslateDetails[]) => {
        const finishedTranslations = await translateAll(outputLanguages);

        if (finishedTranslations) {
            let newTranslations = { ...translations };
            
            outputLanguages.forEach(lang => { 
                const translation = finishedTranslations[lang.lcid];
                if (translation) {
                    newTranslations[lang.lcid] = {
                        ...translation,
                        header: translation.header.length > 0 ? `<p><em>[${lcidMappings[lang.lcid].autoTranslateText}]</em></p>${translation.header}` : "",
                        body: translation.body.length > 0 ? `<p><em>[${lcidMappings[lang.lcid].autoTranslateText}]</em></p>${translation.body}` : "",
                        footer: translation.footer.length > 0 ? `<p><em>[${lcidMappings[lang.lcid].autoTranslateText}]</em></p>${translation.footer}` : "",
                    };
                }
            });
    
            props.onChangeTranslatedContent(newTranslations);
        }
    }

    return (
        <div style={{width: "100%"}}>
            <TranslateAllIssueDialog
                show={errorModalStatus.showModal}
                onClose={() => errorModalStatus.updateModalOpenStatus(false)}
                totalLanguages={errorModalStatus.expectedLanguages}
                returnedTranslations={errorModalStatus.actualLanguages}
            />
            <LoadingOverlay 
                show={isFetchingTranslations}
            />
            <LanguageBarWithTranslateDialog
                translations={translations}
                defaultContent={DEFAULT_TRANSLATABLE_NEWSLETTER_CONTENT}
                onChangeTranslatedContent={props.onChangeTranslatedContent}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                onTranslate={onTranslate}
                useLanguagesData={props.useLanguagesData}
                requiredTranslationFieldsHasContent={requiredTranslationFieldsHasContent}
                translationHasContent={translationHasContent}
                contentType={TranslationContentType.Newsletter}
                endIcon={<KeyboardArrowDown />}
                showTranslateAllOption
                translateAllDisabled={isFetchingTranslations || props.isFetchingTranslation}
                disableLanguageButtons={isFetchingTranslations || props.isFetchingTranslation}
                translateAll={onTranslateAll}
            />
            <ErrorSnackbar errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage("")}/>
        </div>
    )
}

interface INewsletterLanguageBarProps {
    newsletterContent: TranslatedNewsletterContent;
    onChangeTranslatedContent: (newValue: TranslatedNewsletterContent) => void;
    getNewsletterTranslation: (toTranslate: TranslatableNewsletterContent, targetLcid: string, inputLcid: string, newsletterId: string) => Promise<TranslatableNewsletterContent>;
    newsletterId?: string;
    useLanguagesData: useLanguagesData;
    isFetchingTranslation: boolean;
    setIsFetchingTranslation: (toSet: boolean) => void;
}

export default NewsletterLanguageBar;